import { db } from '@/db/db';
import { IMPORT_FORMAT, IMPORT_VERSION } from '@/constants/app';
import type { Constant, Formula, Item, ProblemTemplate, Session } from '@/domain/types';

/**
 * Rebuilds a stored session as import blocks. Used by "Copy as import block",
 * so earlier classes can be pasted into a chapter-review chat. Output must
 * validate against the importer.
 */

export const EXPORT_ITEMS_PER_PART = 60;

interface BlockOut {
  format: string;
  version: number;
  course: string;
  part: { index: number; of: number };
  session: Record<string, unknown>;
  flashcards: unknown[];
  questions: unknown[];
  problemTemplates: unknown[];
  reference: { formulas: unknown[]; constants: unknown[] };
}

function flashcardOut(i: Item) {
  return {
    id: i.id,
    category: i.category,
    difficulty: i.difficulty,
    front: i.front ?? '',
    back: i.back ?? '',
    ...(i.flag ? { flag: i.flag } : {}),
    ...(i.flagText ? { flagText: i.flagText } : {}),
  };
}

function questionOut(i: Item) {
  const base = {
    id: i.id,
    type: i.type,
    category: i.category,
    difficulty: i.difficulty,
    q: i.q ?? '',
    ...(i.rubric?.length ? { rubric: i.rubric } : {}),
    ...(i.note ? { note: i.note } : {}),
    ...(i.flag ? { flag: i.flag } : {}),
    ...(i.flagText ? { flagText: i.flagText } : {}),
  };
  if (i.type === 'mc') {
    return { ...base, opts: i.opts ?? [], answerIndex: i.answerIndex ?? 0, ...(i.sample ? { sample: i.sample } : {}) };
  }
  if (i.type === 'free') {
    return {
      ...base,
      parts: (i.parts ?? []).map((p) => ({
        label: p.label,
        prompt: p.prompt,
        sample: p.sample,
        ...(p.rubric?.length ? { rubric: p.rubric } : {}),
      })),
    };
  }
  if (i.type === 'work') {
    return { ...base, steps: i.steps ?? [], answer: i.answer ?? '' };
  }
  return { ...base, sample: i.sample ?? '' };
}

function templateOut(t: ProblemTemplate) {
  return {
    id: t.id,
    category: t.category,
    difficulty: t.difficulty,
    prompt: t.prompt,
    variables: t.variables,
    constraints: t.constraints,
    compute: t.compute,
    answerUnits: t.answerUnits,
    unitAliases: t.unitAliases,
    sigfigRule: t.sigfigRule,
    tolerance: t.tolerance,
    steps: t.steps,
  };
}

function formulaOut(f: Formula) {
  return {
    id: f.id,
    name: f.name,
    expr: f.expr,
    topic: f.topic,
    ...(f.notes ? { notes: f.notes } : {}),
  };
}

function constantOut(c: Constant) {
  return {
    id: c.id,
    name: c.name,
    symbol: c.symbol,
    value: c.value,
    ...(c.units ? { units: c.units } : {}),
  };
}

export interface SessionExport {
  blocks: BlockOut[];
  text: string;
}

export async function exportSessionBlocks(sessionId: string): Promise<SessionExport> {
  const session = await db.sessions.get(sessionId);
  if (!session || session.deleted) throw new Error(`Session "${sessionId}" was not found.`);

  const items = (await db.items.where('sessionId').equals(sessionId).toArray())
    .filter((i) => !i.deleted)
    .sort((a, b) => a.order - b.order);
  const templates = (await db.templates.where('sessionId').equals(sessionId).toArray()).filter(
    (t) => !t.deleted,
  );
  const formulas = (await db.formulas.where('sessionId').equals(sessionId).toArray()).filter(
    (f) => !f.deleted,
  );
  const constants = (await db.constants.where('sessionId').equals(sessionId).toArray()).filter(
    (c) => !c.deleted,
  );

  const chunks = chunk(items, EXPORT_ITEMS_PER_PART);
  const of = Math.max(1, chunks.length);
  const blocks: BlockOut[] = [];

  for (let index = 0; index < of; index += 1) {
    const part = chunks[index] ?? [];
    const first = index === 0;
    blocks.push({
      format: IMPORT_FORMAT,
      version: IMPORT_VERSION,
      course: session.course,
      part: { index: index + 1, of },
      session: first ? fullSession(session) : { id: session.id },
      flashcards: part.filter((i) => i.type === 'flashcard').map(flashcardOut),
      questions: part.filter((i) => i.type !== 'flashcard').map(questionOut),
      // Templates and reference ride along with part 1 only, so they are never
      // written twice.
      problemTemplates: first ? templates.map(templateOut) : [],
      reference: first
        ? { formulas: formulas.map(formulaOut), constants: constants.map(constantOut) }
        : { formulas: [], constants: [] },
    });
  }

  return { blocks, text: blocks.map((b) => JSON.stringify(b, null, 2)).join('\n\n') };
}

function fullSession(s: Session): Record<string, unknown> {
  return {
    id: s.id,
    kind: s.kind,
    number: s.number,
    title: s.title,
    shortLabel: s.shortLabel,
    addedAt: s.addedAt,
  };
}

function chunk<T>(rows: T[], size: number): T[][] {
  if (rows.length === 0) return [];
  const out: T[][] = [];
  for (let i = 0; i < rows.length; i += size) out.push(rows.slice(i, i + size));
  return out;
}
